<script setup lang="ts">
import { ref, reactive, StyleValue, watchEffect, watch } from "vue";
import { Timeline, Easing, hasClass } from "@zyzgroup/core-web";
import Overlay from "./Overlay.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Overlay, Box },
  inheritAttrs: true
});

// 需传入 key 使子元素重新创建，从而使浏览器计算 contentStyle inset 正确
const props = withDefaults(
  defineProps<{
    closeWhenClickOverlay?: boolean;
    directionFrom?:
      | "bottom"
      | "top"
      | "left"
      | "right"
      | "center"
      | "full"
      | { left: string; top: string }; // absolute，例如 {left: '0%', top: '0%'}
    dur?: number;
    global?: boolean;
    contentBgColor?: string;
    fixed?: number; // 单位px, 强制设置弹起高度或宽度, 如果不传，默认为 slot-width or slot-height
  }>(),
  {
    closeWhenClickOverlay: true,
    directionFrom: "bottom",
    dur: 200,
    global: true,
    contentBgColor: "rgba(255,255,255,0)"
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "resize", rect: DOMRect): void;
  (event: "clickOverlay"): void;
}>();

const overlayRef = ref();
const contentRef = ref();
const contentStyle = reactive<{
  // 特性属性
  width?: string;
  height?: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
  // 通用属性
  display: string;
  position: string;
  opacity?: String;
  transformOrigin?: string;
  willChange?: string; // 优化动画性能, 让浏览器提前为动画做准备，避免抖动或延迟
  backgroundColor?: string;
  inset?: string; // top right bottom left
}>({
  display: "block",
  position: "absolute",
  opacity: "1",
  transformOrigin: "center",
  willChange: "transform",
  backgroundColor: props.contentBgColor
});

let status: "open" | "close" = "close";
let isAnimating = false;

// Overlay.open() Overlay.close() 会触发 Box@resize()
// 有时子元素初始化为空时，即 box.rect.width = 0 && box.rect.height = 0，也要触发一下
let boxWidth = -1;
let boxHeight = -1;
const boxResize = (box) => {
  // box.rect.x 或 box.rect.y 变化时不触发，即：运动过程中不触发
  if (boxWidth != box.rect.width || boxHeight != box.rect.height) {
    boxWidth = box.rect.width;
    boxHeight = box.rect.height;
    executeAnimate();
    emits("resize", box);
  }
};

const boxStyle = reactive<{
  width?: string;
  height?: string;
}>({});
watchEffect(() => {
  switch (props.directionFrom) {
    case "left":
    case "right":
      delete boxStyle.width;
      boxStyle.height = "100%";
      break;
    case "top":
    case "bottom":
      boxStyle.width = "100%";
      delete boxStyle.height;
      break;
    case "full":
      boxStyle.width = "100%";
      boxStyle.height = "100%";
      break;
    case "center":
    default:
      delete boxStyle.width;
      delete boxStyle.height;
      break;
  }
  // 实时更新：子元素自己计算的位置
  if (typeof props.directionFrom == "object") {
    contentStyle.left = props.directionFrom.left;
    contentStyle.top = props.directionFrom.top;
  }
});

function isDirectionFromSupportAnimate(): boolean {
  switch (props.directionFrom) {
    case "bottom":
    case "top":
    case "left":
    case "right":
    case "center":
      return true;
    case "full":
    default: // absolute
      return false;
  }
}

const tl = new Timeline({ frameDuration: props.dur });
const executeAnimate = () => {
  tl.clear();

  switch (props.directionFrom) {
    case "left":
      delete contentStyle.width;
      if (props.fixed) {
        contentStyle.width = props.fixed + "px";
      }
      contentStyle.height = "100%";
      contentStyle.left = isAnimating ? "-100%" : "0%";
      delete contentStyle.right;
      delete contentStyle.bottom;
      contentStyle.top = "0%";
      if (isAnimating) {
        tl.addTween(
          {
            left: 100
          },
          { left: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              contentStyle.left = `-${data.left}%`;
            },
            onComplete: onAnimateComplete
          }
        );
      }
      break;
    case "right":
      delete contentStyle.width;
      if (props.fixed) {
        contentStyle.width = props.fixed + "px";
      }
      contentStyle.height = "100%";
      delete contentStyle.left;
      contentStyle.right = isAnimating ? "-100%" : "0%";
      delete contentStyle.bottom;
      contentStyle.top = "0%";
      if (isAnimating) {
        tl.addTween(
          {
            right: 100
          },
          { right: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              contentStyle.right = `-${data.right}%`;
            },
            onComplete: onAnimateComplete
          }
        );
      }
      break;
    case "top":
      contentStyle.width = "100%";
      delete contentStyle.height;
      if (props.fixed) {
        contentStyle.height = props.fixed + "px";
      }
      contentStyle.left = "0%";
      delete contentStyle.right;
      delete contentStyle.bottom;
      contentStyle.top = isAnimating ? "-100%" : "0%";
      if (isAnimating) {
        tl.addTween(
          {
            top: 100
          },
          { top: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              contentStyle.top = `-${data.top}%`;
            },
            onComplete: onAnimateComplete
          }
        );
      }
      break;
    case "bottom":
      contentStyle.width = "100%";
      delete contentStyle.height;
      if (props.fixed) {
        contentStyle.height = props.fixed + "px";
      }
      contentStyle.left = "0%";
      delete contentStyle.right;
      contentStyle.bottom = isAnimating ? "-100%" : "0%";
      delete contentStyle.top;
      if (isAnimating) {
        tl.addTween(
          {
            bottom: 100
          },
          { bottom: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              contentStyle.bottom = `-${data.bottom}%`;
            },
            onComplete: onAnimateComplete
          }
        );
      }
      break;
    case "center":
      delete contentStyle.width;
      delete contentStyle.height;
      delete contentStyle.right;
      delete contentStyle.bottom;
      contentStyle.left = "50%";
      contentStyle.top = "50%";
      contentStyle.transform = isAnimating
        ? `translate(-50%, -50%) scale(${0.8})`
        : `translate(-50%, -50%) scale(${1})`;
      contentStyle.opacity = isAnimating ? "0" : "1";
      if (isAnimating) {
        tl.addTween(
          {
            scale: 0.8,
            opacity: 0
          },
          {
            scale: 1,
            opacity: 1
          },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              contentStyle.transform = `translate(-50%, -50%) scale(${data.scale})`;
              contentStyle.opacity = `${data.opacity}`;
            },
            onComplete: onAnimateComplete
          }
        );
      }
      break;
    case "full":
      contentStyle.width = "100%";
      contentStyle.height = "100%";
      delete contentStyle.right;
      delete contentStyle.bottom;
      contentStyle.left = "0%";
      contentStyle.top = "0%";
      if (isAnimating) {
        onAnimateComplete();
      }
      break;
    default: // absolute
      delete contentStyle.width;
      delete contentStyle.height;
      delete contentStyle.right;
      delete contentStyle.bottom;
      contentStyle.left = props.directionFrom.left;
      contentStyle.top = props.directionFrom.top;
      if (isAnimating) {
        onAnimateComplete();
      }
      break;
  }

  // transform + opacity
  switch (props.directionFrom) {
    case "center":
      break;
    default:
      delete contentStyle.transform;
      contentStyle.opacity = "1";
      break;
  }

  if (isAnimating && isDirectionFromSupportAnimate()) {
    if (status == "open") {
      tl.play();
    } else {
      tl.reverse();
    }
  }
};

const popOpen = () => {
  // do nothing
};

const popClose = () => {
  boxWidth = -1;
  boxHeight = -1;
  emits("close");
};

function onAnimateComplete() {
  isAnimating = false;
  switch (status) {
    case "open":
      emits("open");
      break;
    case "close":
      overlayRef.value?.close();
      break;
  }
}

function open() {
  status = "open";
  isAnimating = true;
  overlayRef.value?.open();
  // 等待 Box@resize
}

function close() {
  status = "close";
  isAnimating = true;
  if (isDirectionFromSupportAnimate()) {
    executeAnimate();
  } else {
    onAnimateComplete();
  }
}

function clickOverlay(e: Event) {
  // console.log(e.target);
  // console.log(popRef.value.contains(e.target));
  const className =
    props.directionFrom == "full"
      ? // ||
        // (typeof props.directionFrom == "object" &&
        //   props.directionFrom.left == "0%" &&
        //   props.directionFrom.top == "0%") // 默认 boxWidth==100% boxHeight==100%
        "pop-content"
      : "pop";
  if (e.target && hasClass(e.target as HTMLElement, className)) {
    if (props.closeWhenClickOverlay) {
      close();
    }
    emits("clickOverlay");
  }
}

defineExpose({ open, close });
</script>

<template>
  <Overlay
    ref="overlayRef"
    class="pop"
    :global="props.global"
    @open="popOpen"
    @close="popClose"
    @click="clickOverlay"
  >
    <div
      ref="contentRef"
      class="pop-content"
      :class="
        typeof props.directionFrom == 'string'
          ? props.directionFrom
          : 'absolute'
      "
      :style="contentStyle as StyleValue"
    >
      <Box
        display="shrink"
        :style="boxStyle"
        @resize="boxResize"
      >
        <slot></slot>
      </Box>
    </div>
  </Overlay>
</template>
